import { ClickAwayListener, Paper, Popper, useTheme } from "@mui/material";
import ErrorHelper from "./ErrorWrapper";
import Flex from "./Flex";
import { useFieldLock } from "./ReadOnlyFields";
import { MData, MultiSelectProps } from "./mask-multi-select/types";

export type { MData, MultiSelectProps };
import { MultiSelectTrigger } from "./mask-multi-select/MultiSelectTrigger";
import { OptionsList } from "./mask-multi-select/OptionsList";
import { SearchHeader } from "./mask-multi-select/SearchHeader";
import { SelectedOptionsSection } from "./mask-multi-select/SelectedOptionsSection";
import { useMaskMultiSelect } from "./mask-multi-select/use-mask-multi-select";

/**
 * MaskMultiSelect — multi-select dropdown matching the design-system spec
 *
 * Search input lives inside the popup with a results-count badge. Selected
 * items render at the top of the popup with a "Show All / Hide All Selected
 * Options" collapse toggle. Empty search results render a "No Result Found"
 * state.
 *
 * Notes for callers: options must be objects whose first key is a stable id
 * and whose second key is the display label (e.g. `{ uuid, name }`). This
 * positional convention is preserved from the previous implementation.
 *
 * (Figma: eCarehealth DS v0.1 / Dropdown component, node 1159-18386).
 * @figma [https://www.figma.com/design/KyWP0AsqGLoNA5GdK8paFI/eCarehealth-DS-v.0.1-?node-id=767-18164&t=g3GoSNPlJWK4gL81-0]
 *
 * @author [Rajkumar Rathod]
 */
export const MaskMultiSelect = <T extends MData>({
  label,
  required,
  flexProps,
  placeholder = "Select Option from Dropdown",
  options,
  error,
  name,
  handleBlur,
  value = [],
  onChange,
  readOnly,
  disabled,
  onSearch,
  limitTags = 1,
  optionWithSelectAll = false,
  renderCustomLabel,
}: MultiSelectProps<T>) => {
  const theme = useTheme();
  const lock = useFieldLock(disabled || readOnly);
  const controller = useMaskMultiSelect<T>({
    options,
    value,
    onChange,
    onSearch,
    handleBlur,
    name,
    disabled: lock.disabled,
    readOnly: lock.readOnly,
  });

  return (
    <form autoComplete="off">
      <Flex {...flexProps} flexDirection={flexProps?.flexDirection ?? "column"}>
        <ErrorHelper error={error}>
          <MultiSelectTrigger
            anchorRef={controller.anchorRef}
            label={label}
            required={required}
            placeholder={placeholder}
            name={name}
            error={error}
            disabled={lock.disabled}
            readOnly={lock.readOnly}
            open={controller.open}
            value={value}
            idKey={controller.idKey}
            labelKey={controller.labelKey}
            limitTags={limitTags}
            onTogglePaper={
              controller.open ? controller.closePopup : controller.openPopup
            }
          />

          <Popper
            open={controller.open}
            anchorEl={controller.anchorRef.current}
            placement="bottom-start"
            style={{
              zIndex: theme.zIndex.modal,
              width: controller.anchorRef.current?.offsetWidth,
            }}
            modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
          >
            <ClickAwayListener onClickAway={controller.closePopup}>
              <Paper
                elevation={4}
                sx={{
                  width: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <SelectedOptionsSection
                  selectedItems={controller.selectedItems}
                  labelKey={controller.labelKey}
                  showAllSelected={controller.showAllSelected}
                  onToggleShowAll={controller.toggleShowAllSelected}
                  onToggleOption={controller.toggleOption}
                  onClearAll={controller.clearAll}
                  renderCustomLabel={renderCustomLabel}
                />

                <SearchHeader
                  value={controller.searchTerm}
                  resultCount={controller.unselectedFiltered.length}
                  onChange={controller.handleSearchChange}
                />

                <OptionsList
                  options={controller.unselectedFiltered}
                  labelKey={controller.labelKey}
                  idKey={controller.idKey}
                  showSelectAll={optionWithSelectAll}
                  allSelected={controller.allSelected}
                  onToggleSelectAll={controller.toggleSelectAll}
                  onToggleOption={controller.toggleOption}
                  renderCustomLabel={renderCustomLabel}
                />
              </Paper>
            </ClickAwayListener>
          </Popper>
        </ErrorHelper>
      </Flex>
    </form>
  );
};

export default MaskMultiSelect;
